import React, { useContext, useEffect, useRef, useState } from "react";
import { Button, FormControlLabel, Radio, RadioGroup } from "@material-ui/core";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import { PaletteOutlined, SaveOutlined } from "@material-ui/icons";
import ColorModeContext from "../../layout/themeContext";
import { normalizeUiPalette, uiPalettes } from "../../theme/crmColors";
import api from "../../services/api";
import useSettingsStyles from "../../pages/SettingsCustom/styles";

const useStyles = makeStyles(theme => ({
  choices: { display: "grid", gridTemplateColumns: "repeat(2,minmax(0,1fr))", gap: 16,
    [theme.breakpoints.down("xs")]: { gridTemplateColumns: "minmax(0,1fr)" } },
  choice: { display: "flex", margin: 0, padding: "12px 14px", border: "1px solid " + theme.crm.border,
    borderRadius: 10, background: theme.crm.inset, minWidth: 0,
    "& .MuiFormControlLabel-label": { flex: 1, minWidth: 0 },
    "&:focus-within": { outline: "2px solid " + theme.crm.focus, outlineOffset: 2 } },
  selected: { borderColor: theme.crm.accent, background: theme.crm.accentSoft },
  name: { display: "block", fontWeight: 600, marginBottom: 10 },
  swatches: { display: "flex", gap: 6, "& span": { height: 23, flex: "0 1 42px",
    borderRadius: 5, border: "1px solid " + theme.crm.border } }
}));

export default function UiPaletteSelector({ settings = [], onSaved, onBusyChange, disabled = false }) {
  const classes = useStyles();
  const shared = useSettingsStyles();
  const theme = useTheme();
  const context = useContext(ColorModeContext);
  const { setUiPalette, getUiPaletteRevision, getUiPalette, beginUiPaletteSave } = context.colorMode || context;
  const settingValue = normalizeUiPalette(settings.find(item => item.key === "uiPalette")?.value);
  const value = normalizeUiPalette(getUiPalette?.() ?? theme.uiPalette ?? settingValue);
  const [draft, setDraft] = useState(value);
  const [saved, setSaved] = useState(value);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [status, setStatus] = useState("");
  const active = useRef(true);
  const confirmed = useRef(value);
  const locked = useRef(false);
  useEffect(() => { active.current = true; return () => { active.current = false; }; }, []);
  useEffect(() => {
    const old = confirmed.current;
    confirmed.current = value; setSaved(value);
    setDraft(current => current === old ? value : current);
    if (getUiPalette && getUiPalette() === undefined && setUiPalette) setUiPalette(settingValue);
  }, [value, settingValue, getUiPalette, setUiPalette]);
  const save = async event => {
    event.preventDefault();
    if (locked.current || disabled || draft === saved) return;
    locked.current = true; setBusy(true); setError(""); setStatus(""); if (onBusyChange) onBusyChange(true);
    const endSave = beginUiPaletteSave?.();
    const revision = getUiPaletteRevision?.();
    const changes = [{ key: "uiPalette", value: draft }];
    try {
      await api.put("/settings/batch", { settings: changes }, { timeout: 15000 });
      if (!active.current) return;
      const newer = getUiPaletteRevision && revision !== getUiPaletteRevision();
      const value = newer && getUiPalette ? normalizeUiPalette(getUiPalette()) : draft;
      if (!newer && setUiPalette) setUiPalette(value);
      confirmed.current = value; setSaved(value); setDraft(value);
      if (onSaved) onSaved([{ key: "uiPalette", value }]); setStatus(value === draft ? "Paleta salva" : "Paleta atualizada");
    } catch (_) {
      if (active.current) setError("Não foi possível salvar a paleta. Tente novamente.");
    } finally {
      if (endSave) endSave();
      locked.current = false;
      if (active.current) { setBusy(false); if (onBusyChange) onBusyChange(false); }
    }
  };
  return <section className={shared.section} aria-labelledby="palette-title">
    <h2 className={shared.sectionTitle} id="palette-title"><PaletteOutlined />Paleta de cores</h2>
    <form onSubmit={save}>
      <RadioGroup className={classes.choices} aria-label="Paleta de cores" name="uiPalette" value={draft}
        onChange={event => { setDraft(event.target.value); setStatus(""); setError(""); }}>
        {uiPalettes.map(preset => {
          const colors = preset[theme.palette.type === "dark" ? "dark" : "light"];
          return <FormControlLabel key={preset.id} value={preset.id} disabled={busy || disabled}
            className={classes.choice + (draft === preset.id ? " " + classes.selected : "")}
            control={<Radio color="primary" />}
            label={<span><span className={classes.name}>{preset.name}</span><span className={classes.swatches} aria-hidden="true">
              {[colors.background, colors.surface, colors.raised, colors.accent].map((color, index) => <span key={index} style={{ backgroundColor: color }} />)}
            </span></span>} />;
        })}
      </RadioGroup>
      {error && <p role="alert" className={shared.errorText}>{error}</p>}
      <div className={shared.actions}><span role="status">{status}</span>
        <Button type="submit" variant="contained" color="primary" startIcon={<SaveOutlined />} disabled={busy || disabled || draft === saved}>
          {busy ? "Salvando paleta…" : "Salvar paleta"}
        </Button>
      </div>
    </form>
  </section>;
}
